import React from 'react';
import { cn } from '../../utils/cn';

/**
 * PageLayout component for consistent page structure
 *
 * @param {Object} props
 * @param {string} props.title - Page title
 * @param {string} props.subtitle - Optional page subtitle
 * @param {ReactNode} props.headerActions - Actions to show in the header
 * @param {ReactNode} props.children - Page content
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.fullWidth - Whether to use full width layout
 * @param {ReactNode} props.breadcrumbs - Breadcrumb navigation
 */
const PageLayout = ({
    title,
    subtitle,
    headerActions,
    children,
    className,
    fullWidth = false,
    breadcrumbs,
    ...props
}) => {
    return (
        <div
            className={cn(
                'min-h-screen bg-gradient-to-br from-gray-50 to-blue-50',
                className
            )}
            {...props}
        >
            {/* Page Header */}
            <div className="bg-white border-b border-gray-200">
                <div className={cn(
                    'px-4 sm:px-6 lg:px-8 py-6',
                    !fullWidth && 'max-w-7xl mx-auto w-full'
                )}>
                    {/* Breadcrumbs */}
                    {breadcrumbs && (
                        <div className="mb-4">
                            {breadcrumbs}
                        </div>
                    )}

                    {/* Title and Actions */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="min-w-0 flex-1">
                            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 truncate">
                                {title}
                            </h1>
                            {subtitle && (
                                <p className="mt-1 text-sm text-gray-600">
                                    {subtitle}
                                </p>
                            )}
                        </div>

                        {/* Header Actions */}
                        {headerActions && (
                            <div className="flex items-center space-x-3">
                                {headerActions}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Page Content */}
            <div className={cn(
                'px-4 sm:px-6 lg:px-8 py-6',
                !fullWidth && 'max-w-7xl mx-auto w-full'
            )}>
                {children}
            </div>
        </div>
    );
};

/**
 * PageSection component for grouping content within pages
 */
const PageSection = ({
    title,
    subtitle,
    children,
    className,
    actions,
    ...props
}) => (
    <div
        className={cn(
            'space-y-6',
            className
        )}
        {...props}
    >
        {/* Section Header */}
        {(title || actions) && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    {title && (
                        <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                            {title}
                        </h2>
                    )}
                    {subtitle && (
                        <p className="mt-1 text-sm text-gray-600">
                            {subtitle}
                        </p>
                    )}
                </div>

                {actions && (
                    <div className="flex items-center space-x-3">
                        {actions}
                    </div>
                )}
            </div>
        )}

        {/* Section Content */}
        {children}
    </div>
);

/**
 * PageGrid component for responsive grid layouts
 */
const PageGrid = ({
    children,
    columns = { default: 1, sm: 2, lg: 3, xl: 4 },
    gap = 6,
    className,
    ...props
}) => {
    const gridClasses = {
        1: 'grid-cols-1',
        2: 'grid-cols-1 sm:grid-cols-2',
        3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
        4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
        5: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5',
        6: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6',
    };

    return (
        <div
            className={cn(
                'grid gap-6',
                gridClasses[columns.default || columns] || gridClasses[1],
                className
            )}
            {...props}
        >
            {children}
        </div>
    );
};

export { PageLayout, PageSection, PageGrid };